/**
 * Reading transcripts into cues.
 *
 * Meeting and video tools export the same thing — who said what, and when — in several
 * layouts. This turns each of them into one list of cues (a start time when there is one,
 * a speaker when there is one, and the words) so that cleaning does not depend on where
 * the transcript came from:
 *
 * - **WebVTT and SRT** — numbered or identified cues with a `start --> end` line. Teams
 *   marks the speaker with a `<v Name>` voice tag; Zoom writes `Name: words` inside the cue.
 * - **Zoom text** — a `[Name] 00:00:05` line, then the words.
 * - **Timestamps on their own lines** — what YouTube's "Show transcript" panel copies.
 *   Also handled: a timestamp at the start of a line, and `Name   0:05` on one line.
 * - **Plain text** — optionally with `Name: words` speaker labels.
 */

export type TranscriptFormat = "vtt" | "srt" | "zoom" | "stamped" | "plain";

export const TRANSCRIPT_FORMATS: readonly TranscriptFormat[] = [
  "vtt",
  "srt",
  "zoom",
  "stamped",
  "plain",
];

export interface Cue {
  /** Start time in milliseconds, when the transcript gives one. */
  start: number | null;
  speaker: string | null;
  /** The cue's lines, kept apart so rolling captions can be de-duplicated. */
  lines: string[];
}

const TIME = "(?:(\\d{1,2}):)?(\\d{1,2}):(\\d{2})(?:[.,](\\d{1,3}))?";
const RE_TIME = new RegExp(TIME);
const RE_CUE_TIMING = new RegExp(`${TIME}\\s*-->\\s*${TIME}`);
const RE_SRT_COMMA = /\d,\d{3}\s*-->/;
const RE_ZOOM_HEAD = /^\[([^\]\n]{1,80})\]\s+((?:\d{1,2}:)?\d{1,2}:\d{2})\s*$/;
const RE_STAMP_ONLY =
  /^[[(]?((?:\d{1,2}:)?\d{1,2}:\d{2}(?:[.,]\d{1,3})?)[\])]?$/;
const RE_STAMP_LEAD =
  /^[[(]?((?:\d{1,2}:)?\d{1,2}:\d{2}(?:[.,]\d{1,3})?)[\])]?[ \t]+(\S.*)$/;
const RE_SPEAKER_TIME =
  /^(\p{L}[^\n[\]:]{0,60}?)(?: {2,}|\t+)[[(]?((?:\d{1,2}:)?\d{1,2}:\d{2})[\])]?$/u;
const RE_SPEAKER_PREFIX = /^(\p{L}[\p{L}\p{N} .'’-]{0,40}):[ \t]+(\S.*)$/u;
const RE_VOICE = /<v(?:\.[^\s>]+)*\s+([^>]+)>/;
const RE_VOICE_ANY = /<v[^>]*>|<\/v>/g;
const RE_TAGS = /<[^>]*>/g;
const RE_ASS = /\{\\[^}]*\}/g;
const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_LINES = /\r\n|\r|\n/;
const RE_DASH_LINE = /^[-–—]\s*/;
const RE_BOM = /^\uFEFF/;
const RE_NBSP = / /g;
const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
  "&lrm;": "",
  "&rlm;": "",
};
const RE_ENTITY = /&(?:amp|lt|gt|quot|#39|apos|nbsp|lrm|rlm);/g;
const MS_SECOND = 1000;
const MS_MINUTE = 60_000;
const MS_HOUR = 3_600_000;

/** `1:02:03.5`, `02:03,500`, `0:07` → milliseconds. */
export function parseTimestamp(value: string): number | null {
  const match = value.match(RE_TIME);
  if (!match) {
    return null;
  }
  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  const fraction = Number((match[4] ?? "0").padEnd(3, "0"));
  return hours * MS_HOUR + minutes * MS_MINUTE + seconds * MS_SECOND + fraction;
}

export function decodeEntities(text: string): string {
  return text.replace(RE_ENTITY, (entity) => ENTITIES[entity] ?? entity);
}

export function detectFormat(input: string): TranscriptFormat {
  if (RE_CUE_TIMING.test(input)) {
    return RE_SRT_COMMA.test(input) && !input.trimStart().startsWith("WEBVTT")
      ? "srt"
      : "vtt";
  }
  const lines = input.split(RE_LINES).map((line) => line.trim());
  if (lines.some((line) => RE_ZOOM_HEAD.test(line))) {
    return "zoom";
  }
  const stamped = lines.filter(
    (line) =>
      RE_STAMP_ONLY.test(line) ||
      RE_STAMP_LEAD.test(line) ||
      RE_SPEAKER_TIME.test(line)
  );
  return stamped.length >= 2 ? "stamped" : "plain";
}

function cleanCueLine(line: string): { text: string; speaker: string | null } {
  const speaker = line.match(RE_VOICE)?.[1]?.trim() ?? null;
  const text = decodeEntities(
    line
      .replace(RE_VOICE_ANY, "")
      .replace(RE_TAGS, "")
      .replace(RE_ASS, "")
      .replace(RE_NBSP, " ")
  ).trim();
  return { text, speaker };
}

/** SRT dialogue convention: two or more lines starting with a dash are two speakers. */
function splitDashLines(lines: readonly string[]): string[][] {
  if (lines.filter((line) => RE_DASH_LINE.test(line)).length < 2) {
    return [[...lines]];
  }
  return lines
    .filter((line) => line !== "")
    .map((line) => [line.replace(RE_DASH_LINE, "")]);
}

function parseTimedBlock(block: string): Cue[] {
  const lines = block.split(RE_LINES);
  const at = lines.findIndex((line) => RE_CUE_TIMING.test(line));
  if (at < 0) {
    return [];
  }
  const timing = (lines[at] ?? "").match(RE_CUE_TIMING);
  const start = timing ? parseTimestamp(timing[0]) : null;
  let speaker: string | null = null;
  const cleaned: string[] = [];
  for (const raw of lines.slice(at + 1)) {
    const line = cleanCueLine(raw);
    speaker ??= line.speaker;
    if (line.text !== "") {
      cleaned.push(line.text);
    }
  }
  return splitDashLines(cleaned).map((group) => ({
    start,
    speaker,
    lines: group,
  }));
}

function parseTimed(input: string): Cue[] {
  return input
    .replace(RE_BOM, "")
    .split(RE_BLANK_LINE)
    .flatMap(parseTimedBlock);
}

function parseZoom(input: string): Cue[] {
  const cues: Cue[] = [];
  let current: Cue | null = null;
  for (const raw of input.split(RE_LINES)) {
    const line = raw.trim();
    const head = line.match(RE_ZOOM_HEAD);
    if (head) {
      current = {
        start: parseTimestamp(head[2] ?? ""),
        speaker: (head[1] ?? "").trim(),
        lines: [],
      };
      cues.push(current);
    } else if (line !== "" && current) {
      current.lines.push(line);
    }
  }
  return cues;
}

function newCue(start: string, speaker: string | null, text?: string): Cue {
  return {
    start: parseTimestamp(start),
    speaker,
    lines: text ? [text] : [],
  };
}

function parseStamped(input: string): Cue[] {
  const cues: Cue[] = [];
  for (const raw of input.split(RE_LINES)) {
    const line = raw.trim();
    const only = line.match(RE_STAMP_ONLY);
    const named = line.match(RE_SPEAKER_TIME);
    const lead = line.match(RE_STAMP_LEAD);
    const last = cues.at(-1);
    if (only) {
      cues.push(newCue(only[1] ?? "", null));
    } else if (named) {
      cues.push(newCue(named[2] ?? "", (named[1] ?? "").trim()));
    } else if (lead) {
      cues.push(newCue(lead[1] ?? "", null, lead[2]));
    } else if (line !== "" && last) {
      last.lines.push(line);
    } else if (line !== "") {
      cues.push({ start: null, speaker: null, lines: [line] });
    }
  }
  return cues;
}

function parsePlain(input: string): Cue[] {
  const cues: Cue[] = [];
  for (const paragraph of input.split(RE_BLANK_LINE)) {
    let current: Cue | null = null;
    for (const raw of paragraph.split(RE_LINES)) {
      const line = raw.trim();
      if (line === "") {
        continue;
      }
      if (current && !RE_SPEAKER_PREFIX.test(line)) {
        current.lines.push(line);
      } else {
        current = { start: null, speaker: null, lines: [line] };
        cues.push(current);
      }
    }
  }
  return cues;
}

const PREFIXED_SHARE = 0.6;

/**
 * "Name: words" prefixes are only treated as speakers when they look like a convention
 * rather than a stray colon: the name recurs, or most cues carry such a prefix. A line
 * such as "Note: the room was cold" does not invent a speaker called Note.
 */
function acceptPrefixSpeakers(cues: Cue[]): Cue[] {
  const seen = new Map<string, number>();
  const candidates = cues.map((cue) => {
    const first = cue.speaker
      ? null
      : (cue.lines[0] ?? "").match(RE_SPEAKER_PREFIX);
    if (first?.[1]) {
      seen.set(first[1], (seen.get(first[1]) ?? 0) + 1);
    }
    return first;
  });
  const prefixed = candidates.filter(Boolean).length;
  const widespread = prefixed >= Math.max(1, cues.length * PREFIXED_SHARE);
  return cues.map((cue, index) => {
    const match = candidates[index];
    const name = match?.[1];
    if (!(match && name && (widespread || (seen.get(name) ?? 0) >= 2))) {
      return cue;
    }
    return {
      ...cue,
      speaker: name.trim(),
      lines: [match[2] ?? "", ...cue.lines.slice(1)],
    };
  });
}

export function parseTranscript(
  input: string,
  format: TranscriptFormat
): Cue[] {
  switch (format) {
    case "vtt":
    case "srt":
      return acceptPrefixSpeakers(parseTimed(input));
    case "zoom":
      return parseZoom(input);
    case "stamped":
      return parseStamped(input);
    default:
      return acceptPrefixSpeakers(parsePlain(input));
  }
}
